import React from 'react';
import { Card } from '../ui';
import { MapPin, Navigation, Clock } from 'lucide-react';

const TollSequenceTimeline = ({ tollSequence, loading = false }) => {
  if (loading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded"></div>
          ))}
        </div>
      </Card>
    );
  }

  if (!tollSequence || tollSequence.length === 0) {
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Toll Plaza Sequence</h3>
        <p className="text-gray-500 text-sm">No toll data available</p>
      </Card>
    );
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Toll Plaza Sequence</h3>
        <div className="text-xs text-gray-500">
          {tollSequence.length} toll{tollSequence.length !== 1 ? 's' : ''} passed
        </div>
      </div>

      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        <div className="space-y-6">
          {tollSequence.map((toll, index) => {
            const isFirst = index === 0;
            const isLast = index === tollSequence.length - 1;

            return (
              <div key={toll.sequence_id} className="relative pl-14">
                {/* Timeline node */}
                <div
                  className={`absolute left-4 -ml-2 w-4 h-4 rounded-full border-2 ${
                    isFirst
                      ? 'bg-green-500 border-green-500'
                      : isLast
                      ? 'bg-red-500 border-red-500'
                      : 'bg-blue-500 border-blue-500'
                  }`}
                >
                  <div className="absolute -left-1 -top-1 w-6 h-6 rounded-full bg-current opacity-20 animate-ping"></div>
                </div>

                {/* Sequence number badge */}
                <div
                  className={`absolute left-0 -ml-1 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    isFirst
                      ? 'bg-green-100 text-green-700'
                      : isLast
                      ? 'bg-red-100 text-red-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  {toll.sequence_order}
                </div>

                {/* Toll details card */}
                <div
                  className={`bg-gray-50 rounded-lg p-4 border-l-4 ${
                    isFirst
                      ? 'border-green-500'
                      : isLast
                      ? 'border-red-500'
                      : 'border-blue-500'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-gray-600" />
                      <h4 className="font-semibold text-gray-900">
                        {toll.plaza_name || `Toll ${toll.toll_plaza_id}`}
                      </h4>
                    </div>
                    {isFirst && (
                      <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">
                        Origin
                      </span>
                    )}
                    {isLast && (
                      <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded">
                        Destination
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Clock className="w-3 h-3" />
                      <span>{formatTime(toll.passed_at)}</span>
                    </div>

                    {toll.distance_from_previous_km !== null &&
                      toll.distance_from_previous_km !== undefined && (
                        <div className="flex items-center space-x-2 text-gray-600">
                          <Navigation className="w-3 h-3" />
                          <span>{toll.distance_from_previous_km.toFixed(1)} km from previous</span>
                        </div>
                      )}
                  </div>

                  {toll.highway_name && (
                    <div className="mt-2 text-xs text-gray-500">
                      Highway: {toll.highway_name}
                    </div>
                  )}

                  {toll.state && (
                    <div className="mt-1 text-xs text-gray-500">State: {toll.state}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <p className="text-xs text-gray-500 mb-1">Total Distance</p>
            <p className="text-lg font-bold text-gray-900">
              {tollSequence
                .reduce((sum, toll) => sum + (toll.distance_from_previous_km || 0), 0)
                .toFixed(1)}{' '}
              km
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Journey Duration</p>
            <p className="text-lg font-bold text-gray-900">
              {tollSequence.length > 1 && tollSequence[0].passed_at && tollSequence[tollSequence.length - 1].passed_at
                ? (
                    (new Date(tollSequence[tollSequence.length - 1].passed_at) -
                      new Date(tollSequence[0].passed_at)) /
                    (1000 * 60 * 60)
                  ).toFixed(1)
                : 'N/A'}{' '}
              hrs
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default TollSequenceTimeline;
